import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Button, Spinner, Form } from 'react-bootstrap'
import Select from 'react-select'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateOnly, formatCurrency } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { createColumnToggleDropdown, useTableState } from '../utils/tableUtils'
import '../styles/selectControl.css'

const fmtDec = (val, digits = 2) => {
    if (val === null || val === undefined) return ''
    return Number(val).toFixed(digits)
}

const fmtPct = (val) => {
    if (val === null || val === undefined) return ''
    return Number(val).toFixed(1) + '%'
}

const fmtInt = (val) => {
    if (val === null || val === undefined) return ''
    return Number(val).toLocaleString()
}

function AmazonSearchTermReportDetailScreen() {
    const { reportId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [report, setReport] = useState(null)
    const [reportLoading, setReportLoading] = useState(true)
    const [error, setError] = useState('')

    const [rows, setRows] = useState([])
    const [rowsLoading, setRowsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)
    const [matchTypeFilter, setMatchTypeFilter] = useState('')
    const [matchTypeOptions, setMatchTypeOptions] = useState([])
    const [sortColumn, setSortColumn] = useState('cost')
    const [sortDirection, setSortDirection] = useState('desc')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())

    const initialTableState = {
        visibility: {
            search_term: true, keyword: true, keyword_type: false,
            campaign_name: true, ad_group_name: true,
            match_type: true, targeting: false,
            impressions: true, clicks: true, ctr: true,
            cost: true, cpc: true,
            ad_sales: true, acos: true,
            ad_orders: true, ad_units_sold: true, conv_rate: true,
        },
        widths: {}
    }
    const { columnState, updateColumnVisibility } = useTableState('amazon_search_term_report', initialTableState)
    const columnVisibility = columnState.visibility || initialTableState.visibility
    const toggleColumnVisibility = (columnId) => updateColumnVisibility(columnId, !columnVisibility[columnId])
    const columnLabels = {
        search_term: 'Search Term', keyword: 'Keyword', keyword_type: 'Keyword Type',
        campaign_name: 'Campaign', ad_group_name: 'Ad Group',
        match_type: 'Match Type', targeting: 'Targeting',
        impressions: 'Impressions', clicks: 'Clicks', ctr: 'CTR',
        cost: 'Cost', cpc: 'CPC',
        ad_sales: 'Sales', acos: 'ACoS',
        ad_orders: 'Orders', ad_units_sold: 'Units Sold', conv_rate: 'Conv. Rate',
    }

    const fetchReport = useCallback(async () => {
        setReportLoading(true)
        try {
            const data = await amazonAPI.searchTermReports.get(reportId)
            setReport(data)
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setReportLoading(false)
        }
    }, [reportId])

    useEffect(() => {
        fetchReport()
    }, [fetchReport])

    const fetchRows = useCallback(async () => {
        if (!report || report.status !== 'DONE') return
        setRowsLoading(true)
        setError('')
        try {
            const params = new URLSearchParams({
                page: currentPage,
                page_size: pageSize,
                sortField: sortColumn,
                sortDirection: sortDirection,
            })
            if (searchTerm) params.set('search', searchTerm)
            if (matchTypeFilter) params.set('match_type', matchTypeFilter)

            const data = await amazonAPI.searchTermReports.getRows(report.id, params.toString())
            if (data) {
                setRows(data.results || [])
                setTotalItems(data.count || 0)
            }
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setRowsLoading(false)
        }
    }, [report, currentPage, pageSize, sortColumn, sortDirection, searchTerm, matchTypeFilter])

    useEffect(() => {
        fetchRows()
    }, [fetchRows])

    useEffect(() => {
        if (!report?.match_types) return
        setMatchTypeOptions(report.match_types.filter(Boolean).map(t => ({ value: t, label: t })))
    }, [report])

    const handleSearch = (e) => {
        const value = e?.target?.value ?? e
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value)
            setCurrentPage(1)
        }, 300)
    }

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
        setCurrentPage(1)
    }

    const handleSort = (column, direction) => {
        setSortColumn(column)
        setSortDirection(direction)
        setCurrentPage(1)
    }

    const computeCtr = (row) => {
        if (!row.impressions) return ''
        return fmtPct((row.clicks / row.impressions) * 100)
    }

    const computeCpc = (row) => {
        if (!row.clicks) return ''
        return `$${fmtDec(Number(row.cost) / row.clicks)}`
    }

    const computeAcos = (row) => {
        const sales = Number(row.ad_sales)
        if (!sales) return ''
        return fmtPct((Number(row.cost) / sales) * 100)
    }

    const computeConvRate = (row) => {
        if (!row.clicks) return ''
        return fmtPct((row.ad_orders / row.clicks) * 100)
    }

    const reportColumns = [
        { id: 'search_term', header: 'Search Term', sortable: true,
          cell: (row) => row.search_term || '' },
        { id: 'keyword', header: 'Keyword', sortable: true,
          cell: (row) => row.keyword || '' },
        { id: 'keyword_type', header: 'Keyword Type', sortable: true,
          cell: (row) => row.keyword_type || '' },
        { id: 'campaign_name', header: 'Campaign', sortable: true,
          cell: (row) => row.campaign_name || '' },
        { id: 'ad_group_name', header: 'Ad Group', sortable: true,
          cell: (row) => row.ad_group_name || '' },
        { id: 'match_type', header: 'Match Type', sortable: true,
          cell: (row) => row.match_type || '' },
        { id: 'targeting', header: 'Targeting', sortable: true,
          cell: (row) => row.targeting || '' },
        { id: 'impressions', header: 'Impressions', sortable: true,
          cell: (row) => fmtInt(row.impressions) },
        { id: 'clicks', header: 'Clicks', sortable: true,
          cell: (row) => fmtInt(row.clicks) },
        { id: 'ctr', header: 'CTR',
          cell: (row) => computeCtr(row) },
        { id: 'cost', header: 'Cost', sortable: true,
          cell: (row) => row.cost ? `$${fmtDec(row.cost)}` : '' },
        { id: 'cpc', header: 'CPC',
          cell: (row) => computeCpc(row) },
        { id: 'ad_sales', header: 'Sales', sortable: true,
          cell: (row) => row.ad_sales ? `$${fmtDec(row.ad_sales)}` : '' },
        { id: 'acos', header: 'ACoS',
          cell: (row) => computeAcos(row) },
        { id: 'ad_orders', header: 'Orders', sortable: true,
          cell: (row) => fmtInt(row.ad_orders) },
        { id: 'ad_units_sold', header: 'Units Sold', sortable: true,
          cell: (row) => fmtInt(row.ad_units_sold) },
        { id: 'conv_rate', header: 'Conv. Rate',
          cell: (row) => computeConvRate(row) },
    ].filter(col => columnVisibility[col.id])

    if (reportLoading) {
        return (
            <Container fluid className="px-4 py-4">
                <div className="text-center py-4"><Spinner animation="border" /></div>
            </Container>
        )
    }

    const headerSubtitle = report
        ? `${report.seller_account_name || ''} (${formatDateOnly(report.start_date)} – ${formatDateOnly(report.end_date)})`
        : ''

    const summary = report?.summary

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="mb-0">Search Term Report</h1>
                    {headerSubtitle && <p className="text-muted mb-0">{headerSubtitle}</p>}
                </div>
                <Button variant="outline-primary" onClick={() => navigate('/amazon-search-term-report', { state: { returnFilters } })}>
                    <i className="fas fa-arrow-left me-2"></i>Back to List
                </Button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {rowsLoading && rows.length === 0 ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : (
                <>
                    {summary && (
                        <Row className="mb-4 g-3">
                            <Col><div className="bg-primary text-white p-3 rounded text-center"><h5 className="mb-1">Search Terms</h5><h3 className="mb-0">{(summary.unique_terms || 0).toLocaleString()}</h3></div></Col>
                            <Col><div className="bg-warning text-white p-3 rounded text-center"><h5 className="mb-1">Total Spend</h5><h3 className="mb-0">{formatCurrency(summary.total_spend)}</h3></div></Col>
                            <Col><div className="bg-success text-white p-3 rounded text-center"><h5 className="mb-1">Total Sales</h5><h3 className="mb-0">{formatCurrency(summary.total_sales)}</h3></div></Col>
                            <Col><div className="bg-info text-white p-3 rounded text-center"><h5 className="mb-1">Total Orders</h5><h3 className="mb-0">{(summary.total_orders || 0).toLocaleString()}</h3></div></Col>
                            <Col><div className="bg-danger text-white p-3 rounded text-center"><h5 className="mb-1">Wasted Spend</h5><h3 className="mb-0">{formatCurrency(summary.wasted_spend)}</h3></div></Col>
                        </Row>
                    )}
                    <div className="d-flex align-items-center mb-3 gap-3">
                        <div>{createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}</div>
                        <div style={{ minWidth: '180px' }}>
                            <Select
                                value={matchTypeOptions.find(opt => opt.value === matchTypeFilter) || null}
                                onChange={(opt) => { setMatchTypeFilter(opt?.value || ''); setCurrentPage(1) }}
                                options={matchTypeOptions}
                                isClearable
                                placeholder="Match Type..."
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div className="ms-auto" style={{ maxWidth: '300px' }}>
                            <SearchInputWithClear value={searchInputValue} onChange={handleSearch}
                                onClear={handleClearSearch} placeholder="Search term, keyword, or campaign..." />
                        </div>
                    </div>
                    {rows.length === 0 ? (
                        <p className="text-muted">No search term data found.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <ResizableTable columns={reportColumns} data={rows} tableId="amazon_search_term_report"
                                    enableSorting={true} onSort={handleSort}
                                    sortColumn={sortColumn} sortDirection={sortDirection}
                                    initialColumnWidths={{
                                        search_term: 250, keyword: 200, keyword_type: 110,
                                        campaign_name: 200, ad_group_name: 180,
                                        match_type: 110, targeting: 180,
                                        impressions: 100, clicks: 80, ctr: 70,
                                        cost: 90, cpc: 70,
                                        ad_sales: 90, acos: 70,
                                        ad_orders: 80, ad_units_sold: 90, conv_rate: 90,
                                    }}
                                    striped hover className="table-sm" />
                            </div>
                            <div className="mt-3">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={setPageSize}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </Container>
    )
}

export default AmazonSearchTermReportDetailScreen
